import { useStore } from '@nanostores/react'
import { Fit, Layout, Rive, useRive } from '@rive-app/react-canvas'
import { Howler } from 'howler'
import { animate, type AnimationPlaybackControlsWithThen } from 'motion'
import { useEffect, useRef } from 'react'
import anim from '~/assets/anim.riv'
import { ForestAppBadge } from '~/components/forest-app-badge'
import { CloseButton } from './components/close-button'
import { DevTools } from './components/dev-tools'
import { Dialog } from './components/dialog'
import { FeatureList } from './components/feature-list'
import { FixedBottomCTA } from './components/fixed-bottom-cta'
import { FloatingParticles } from './components/floating-particles'
import { SpotlightGraphics } from './components/spotlight-graphics'
import { initialRevealingRef, Loading } from './lib/loading'
import { m } from './paraglide/messages'
import { debugInitialTheme } from './params'
import { native } from '@forest/native-services'
import { soundByTheme } from './sounds'
import { $chinaAndroidTrialEligibility } from './store/china-android-trial'
import { $isCloseButtonVisible } from './store/close-button'
import { $pageContext } from './store/page-context'
import { $products } from './store/products'

export function App() {
  const pageContext = useStore($pageContext)
  const products = useStore($products)

  const { RiveComponent, rive } = useRive({
    src: anim,
    stateMachines: ['State Machine 1'],
    artboard: 'paywall_relax',
    autoplay: false,
    layout: new Layout({
      fit: Fit.FitHeight,
    }),
    onStateChange: ({ data }) => {
      if (data) {
        const [themeTag] = data as [string]
        if (!(themeTag in soundByTheme)) return
        const sound = soundByTheme[themeTag as keyof typeof soundByTheme]
        initialRevealingRef.current.promise.then(() => {
          if (!$pageContext.get()) return
          sound.play()
        })
      }
    },
    onLoadError: () => {
      // TODO: show error dialog
      native.general.close()
    },
  })

  const setRiveTheme = ({ rive, theme }: { rive: Rive; theme: number }) => {
    rive.stateMachineInputs('State Machine 1').forEach((input) => {
      if (input.name === 'theme') {
        input.value = theme
      }
    })
    rive.play()
    requestAnimationFrame(() => rive.pause())
  }

  const chinaAndroidTrialEligibility = useStore($chinaAndroidTrialEligibility)

  useEffect(() => {
    if (rive && products && chinaAndroidTrialEligibility !== 'pending') {
      native.general.pageReady()
    }
  }, [rive, products, chinaAndroidTrialEligibility])

  const revealingAnimRef = useRef<null | AnimationPlaybackControlsWithThen>(
    null,
  )

  useEffect(() => {
    if (
      rive &&
      products &&
      pageContext &&
      chinaAndroidTrialEligibility !== 'pending'
    ) {
      setTimeout(() => {
        $isCloseButtonVisible.set(true)
      }, 3000)

      setRiveTheme({ rive, theme: pageContext.initial_theme })

      const revealing = animate([
        [
          '#header',
          { opacity: [0, 1] },
          { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
        ],
        [
          '#key-visual',
          { opacity: [0, 1] },
          { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
        ],
        [
          '#features',
          { opacity: [0, 1] },
          { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
        ],
        [
          '#content',
          { opacity: [0, 1] },
          { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
        ],
        [
          '#fixed-bottom-cta',
          { transform: ['translateY(100%)', 'translateY(0%)'] },
          { duration: 0.7, ease: [0.33, 1, 0.68, 1] },
        ],
      ])
      revealingAnimRef.current = revealing

      revealing.then(() => {
        requestAnimationFrame(() => rive.play())
        initialRevealingRef.current.done()
      })
    }
  }, [rive, products, pageContext, chinaAndroidTrialEligibility])

  const handlePageHide = () => {
    Howler.stop()
    $pageContext.set(null)
    setTimeout(() => {
      rive?.reset({
        artboard: 'paywall_relax',
        stateMachines: ['State Machine 1'],
      })
      revealingAnimRef.current!.time = 0
      revealingAnimRef.current!.stop()
      initialRevealingRef.current = new Loading()
    }, 400)
    native.general.close()
  }

  return (
    <>
      <div
        className="relative flex min-h-screen pb-[152px]"
        style={{
          backgroundImage: 'linear-gradient(172deg, #13193A 0%, #000 100%)',
        }}
      >
        {/* Particles floating effect */}
        <FloatingParticles />

        <SpotlightGraphics />

        {/* Main content */}
        <div className="relaitve z-10 mx-auto flex w-full max-w-[640px] flex-col items-center gap-11 px-6 pb-12 pt-[calc(49px+var(--safe-area-top))]">
          {/* Header section */}
          <div
            id="header"
            className="flex w-full flex-col items-center gap-2 opacity-0"
          >
            <div className="flex flex-col items-center gap-3">
              <ForestAppBadge />
              <h1
                className="text-center text-[26px] font-bold leading-9 text-white"
                style={{ textShadow: 'rgba(255,255,255,0.25) 0px 0px 10px' }}
              >
                {m.relax_paywall_soundscape_title()}
              </h1>
            </div>
          </div>

          {/* Key visual rive animation */}
          <div
            id="key-visual"
            className="pointer-events-none relative h-[30.52vh] w-full opacity-0"
          >
            <div className="absolute left-1/2 top-0 z-10 h-full min-w-[100vw] -translate-x-1/2">
              <RiveComponent />
            </div>
          </div>

          {/* Features section */}
          <div
            id="features"
            className="flex w-full flex-col items-center gap-6 opacity-0"
          >
            {/* Title with decorative lines */}
            <div className="flex w-full items-center gap-2">
              <div className="h-[2px] flex-1 rounded-[30px] bg-gradient-to-l from-[rgba(255,255,255,0.4)] to-[rgba(40,15,204,0)]" />
              <p
                className="max-w-[220px] text-center text-base font-semibold text-white"
                style={{ textShadow: 'rgba(255,255,255,0.1) 0px 0px 10px' }}
              >
                {m.daily_paywall_tree_footer()}
              </p>
              <div className="h-[2px] flex-1 rounded-[30px] bg-gradient-to-r from-[rgba(255,255,255,0.4)] to-[rgba(40,15,204,0)]" />
            </div>

            <FeatureList />
          </div>
        </div>
      </div>

      <CloseButton
        className="fixed right-4 top-[calc(var(--safe-area-top)+16px)] z-30"
        onClick={handlePageHide}
      />

      {import.meta.env.DEV && (
        <button
          className="fixed right-4 top-16 z-10"
          onClick={() => {
            setTimeout(() => {
              $pageContext.set({ initial_theme: debugInitialTheme })
            }, 100)
          }}
        >
          Visible
        </button>
      )}

      {import.meta.env.MODE !== 'production' && <DevTools />}

      {products &&
        chinaAndroidTrialEligibility !== 'pending' &&
        pageContext && (
          <FixedBottomCTA
            products={products}
            chinaAndroidTrialEligibility={chinaAndroidTrialEligibility}
          />
        )}

      <Dialog />
    </>
  )
}
